import { HStack } from "@chakra-ui/layout";
import {
  Box,
  Button,
  Divider,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { SingleValue } from "chakra-react-select";
import { FormikErrors, FormikProps, withFormik } from "formik";

import React, { useEffect } from "react";
import { ApiClient, ApiResult } from "../../api/apiClient";
import { CherryPayApi } from "../../api/models";
import {
  ChakraReactSelectField,
  SelectOption,
} from "../../components/fields/SelectField/ChakraReactSelectField";
import { TextField } from "../../components/fields/TextField/TextField";
import { useApiRequest } from "../../hooks/useApiRequest";
import { MemberAddressForm } from "./MemberAddressForm";
import { MemberContactForm } from "./MemberContactForm";
import { emptyStrToNull, isEmptyStr } from "../../util/StringUtil";
import { isValidEmail } from "../../util/isValidEmail";
import { AddressType, AddressTypeList } from "../../enums/AddressType";
import { Card } from "../../components/Card/Card";
import { ContactType, ContactTypeList } from "../../enums/ContactType";
import { v4 as uuid } from "uuid";
import { DateField } from "../../components/DateField/DateField";
import { emptyStringValuesToNull } from "../../util/ObjectUtil";
import { ExtendedFieldInput } from "../../components/ExtendedFieldInput";
import DateUtil from "../../util/DateUtil";

interface MemberFormProps {
  apiClient: ApiClient;
  businessId: string;
  member?: CherryPayApi.Member | null;
  memberFullName: string | null;
  extendedFieldDefinitions?: CherryPayApi.ExtendedFieldDefinition[];
  onCancel: () => void;
  onSuccess: (member: CherryPayApi.Member) => void;
  onFailure: (message?: string) => void;
  onValidationError?: (message: string) => void;
}

export interface MemberFormValues {
  memberKey?: string | null;
  venueId?: string | null;
  membershipNumber?: string | null;
  badgeNumber?: string | null;
  cardNumber?: string | null;
  title?: string | null;
  firstName?: string | null;
  preferredName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  birthDate?: string | null;
  nationality?: string | null;
  dateJoined?: string | null;
  dateRenewed?: string | null;
  dateFinancial?: string | null;
  financial?: boolean | null;
  status?: string | null;
  gender?: string | null;
  occupation?: string | null;
  memberLevel?: string | null;
  memberLevelDescription?: string | null;
  membershipType?: string | null;
  membershipTypeDescription?: string | null;
  defaultAddress?: AddressType;
  homeAddress?: CherryPayApi.Address | null;
  workAddress?: CherryPayApi.Address | null;
  postalAddress?: CherryPayApi.Address | null;
  tempGeocodedAddress?: CherryPayApi.GeocodedAddress | null;
  geocodedHomeAddress?: CherryPayApi.GeocodedAddress | null;
  geocodedWorkAddress?: CherryPayApi.GeocodedAddress | null;
  geocodedPostalAddress?: CherryPayApi.GeocodedAddress | null;
  defaultContactDetails?: ContactType;
  homeContactDetails?: CherryPayApi.Contact;
  workContactDetails?: CherryPayApi.Contact;
  postalContactDetails?: CherryPayApi.Contact;
  pointsBalance?: number | null;
  extendedFields?: { [key: string]: unknown } | null;
  portalRequiredFields?: string[] | null;
}

const InnerForm = ({
  isSubmitting,
  values,
  dirty,
  businessId,
  member,
  memberFullName,
  onCancel,
  onSuccess,
  onFailure,
  setFieldValue,
  submitForm,
}: MemberFormProps & FormikProps<MemberFormValues>) => {
  const { data: venuesOptions, isLoading: venuesOptionsLoading } =
    useApiRequest(
      (apiClient) => apiClient.getHubMembershipVenues(businessId),
      []
    );

  useEffect(() => {
    if (venuesOptions && venuesOptions.length == 1) {
      setFieldValue("venueId", venuesOptions[0].Key);
    }
  }, [venuesOptions]);

  // Fetch venue details to get MemberPortalRequiredFields (only for new members)
  const { data: venueDetails } = useApiRequest(
    (apiClient) =>
      values.venueId && !member
        ? apiClient.getHubMembershipVenueById(businessId, values.venueId)
        : null,
    [values.venueId, member]
  );

  // Update portalRequiredFields when venue details are loaded
  useEffect(() => {
    if (venueDetails && !member) {
      setFieldValue(
        "portalRequiredFields",
        venueDetails.MemberPortalRequiredFields ?? null
      );
    }
  }, [venueDetails, member, setFieldValue]);

  // Helper function to check if a field is required
  // Checks both hardcoded required fields and dynamic portal required fields
  const isFieldRequired = (fieldName: string): boolean => {
    // Hardcoded required fields (always required)
    const hardcodedRequired = ["firstName", "lastName", "membershipType"];
    if (hardcodedRequired.includes(fieldName)) {
      return true;
    }

    // Dynamic required fields from venue configuration (only for new members)
    if (!member && values.portalRequiredFields) {
      // Map form field names to API field names
      const fieldNameMapping: { [key: string]: string } = {
        birthDate: "BirthDate",
        gender: "Gender",
        title: "Title",
        occupation: "Occupation",
        nationality: "Nationality",
      };

      const apiFieldName = fieldNameMapping[fieldName];
      if (apiFieldName && values.portalRequiredFields.includes(apiFieldName)) {
        return true;
      }
    }

    return false;
  };

  // Helper to check if a contact field is required for the default contact type
  const isContactFieldRequired = (contactField: string): boolean => {
    if (member || !values.portalRequiredFields) {
      return false;
    }
    return values.portalRequiredFields.includes(contactField);
  };

  // Determine which contact type is the default (for showing required indicators)
  const defaultContactType = values.defaultContactDetails || ContactType.Home;

  const {
    data: membershipTypesOptions,
    isLoading: membershipTypesOptionsLoading,
  } = useApiRequest(
    (apiClient) =>
      values.venueId || member?.id
        ? apiClient.getHubMembershipTypes(businessId, {
            venueId: values.venueId,
            memberId: member?.id,
          })
        : null,
    [values.venueId, member?.id]
  );

  const {
    data: membershipLevelsOptions,
    isLoading: membershipLevelsOptionsLoading,
  } = useApiRequest(
    (apiClient) =>
      values.venueId || member?.id
        ? apiClient.getHubMembershipLevels(businessId, {
            venueId: values.venueId,
            memberId: member?.id,
          })
        : null,
    [values.venueId, member?.id]
  );

  const { data: gendersOptions, isLoading: gendersOptionsLoading } =
    useApiRequest(
      (apiClient) =>
        values.venueId || member?.id
          ? apiClient.getHubMembershipGenders(businessId, {
              venueId: values.venueId,
              memberId: member?.id,
            })
          : null,
      [values.venueId, member?.id]
    );

  const { data: titlesOptions, isLoading: titlesOptionsLoading } =
    useApiRequest(
      (apiClient) =>
        values.venueId || member?.id
          ? apiClient.getHubMembershipTitles(businessId, {
              venueId: values.venueId,
              memberId: member?.id,
            })
          : null,
      [values.venueId, member?.id]
    );

  const {
    data: extendedFieldDefinitions,
    isLoading: extendedFieldDefinitionsLoading,
  } = useApiRequest(
    (apiClient) => apiClient.getExtendedFieldDefinitions(businessId),
    [businessId]
  );

  const { data: convertedHomeAddress, isLoading: isHomeLoading } =
    useApiRequest(
      (apiClient) =>
        values.geocodedHomeAddress
          ? apiClient.convertHubMembershipGeocodedToAddress(
              businessId,
              values.geocodedHomeAddress!
            )
          : null,
      [values.geocodedHomeAddress]
    );

  const { data: convertedWorkAddress, isLoading: isWorkLoading } =
    useApiRequest(
      (apiClient) =>
        values.geocodedWorkAddress
          ? apiClient.convertHubMembershipGeocodedToAddress(
              businessId,
              values.geocodedWorkAddress!
            )
          : null,
      [values.geocodedWorkAddress]
    );

  const { data: convertedPostalAddress, isLoading: isPostalLoading } =
    useApiRequest(
      (apiClient) =>
        values.geocodedPostalAddress
          ? apiClient.convertHubMembershipGeocodedToAddress(
              businessId,
              values.geocodedPostalAddress!
            )
          : null,
      [values.geocodedPostalAddress]
    );

  useEffect(() => {
    if (dirty) {
      setFieldValue("homeAddress", convertedHomeAddress);
    }
  }, [convertedHomeAddress]);

  useEffect(() => {
    if (dirty) {
      setFieldValue("workAddress", convertedWorkAddress);
    }
  }, [convertedWorkAddress]);

  useEffect(() => {
    if (dirty) {
      setFieldValue("postalAddress", convertedPostalAddress);
    }
  }, [convertedPostalAddress]);

  // Initialize extended field default values when creating a new member
  useEffect(() => {
    // Only run for new members (no member prop) and when definitions are loaded
    // Check if extendedFields is undefined or null (not just falsy, to allow empty objects)
    if (
      !member &&
      extendedFieldDefinitions &&
      extendedFieldDefinitions.length > 0 &&
      (values.extendedFields === undefined || values.extendedFields === null)
    ) {
      const defaultFields: { [key: string]: unknown } = {};

      const hasDefaultValue = (defaultValue: any) => {
        return defaultValue !== undefined && defaultValue !== null;
      };

      const hasDefaultValues = (defaultValues: any) => {
        return (defaultValues?.length ?? 0) > 0;
      };

      extendedFieldDefinitions.forEach((fieldDef) => {
        // Skip read-only and system-writable fields
        if (
          fieldDef.Writability === "ReadOnly" ||
          fieldDef.Writability === "SystemWritable"
        ) {
          return;
        }

        // Apply default values based on field type and attributes
        const fieldId = fieldDef.id;

        switch (fieldDef.FieldType) {
          case "Toggle":
            if (hasDefaultValue(fieldDef.BooleanAttributes?.DefaultValue)) {
              defaultFields[fieldId] = fieldDef.BooleanAttributes?.DefaultValue;
            }
            break;

          case "Date":
            if (hasDefaultValue(fieldDef.DateAttributes?.DefaultValue)) {
              let dateStr = fieldDef.DateAttributes?.DefaultValue as string;
              if (dateStr.includes("T")) {
                dateStr = dateStr.split("T")[0];
              }
              defaultFields[fieldId] = dateStr;
            }
            break;

          case "DateTime":
            if (hasDefaultValue(fieldDef.DateAttributes?.DefaultValue)) {
              const rawValue = fieldDef.DateAttributes?.DefaultValue as string;
              const agnosticValue =
                DateUtil.getTimezoneAgnosticDateTimeFormat(rawValue);
              if (agnosticValue) {
                defaultFields[fieldId] = agnosticValue;
              }
            }
            break;

          case "Time":
            if (hasDefaultValue(fieldDef.TimeAttributes?.DefaultValue)) {
              const timeStr = fieldDef.TimeAttributes?.DefaultValue as string;
              const timeMatch = timeStr.match(/^(\d{2}):(\d{2})/);
              if (timeMatch) {
                defaultFields[fieldId] = `${timeMatch[1]}:${timeMatch[2]}`;
              }
            }
            break;
          case "NumberMultiSelection":
            if (hasDefaultValues(fieldDef.NumberAttributes?.DefaultValues)) {
              defaultFields[fieldId] = (
                fieldDef.NumberAttributes?.DefaultValues as number[]
              ).map((v) => v.toString());
            }
            break;

          case "Number":
          case "NumberSelection":
            if (hasDefaultValues(fieldDef.NumberAttributes?.DefaultValues)) {
              defaultFields[fieldId] = (
                fieldDef.NumberAttributes?.DefaultValues as number[]
              )[0].toString();
            }
            break;

          case "TextMultiSelection":
            if (hasDefaultValues(fieldDef.StringAttributes?.DefaultValues)) {
              defaultFields[fieldId] = [
                ...(fieldDef.StringAttributes?.DefaultValues as string[]),
              ];
            }
            break;

          case "Text":
          case "TextArea":
          case "TextSelection":
            if (hasDefaultValues(fieldDef.StringAttributes?.DefaultValues)) {
              defaultFields[fieldId] = (
                fieldDef.StringAttributes?.DefaultValues as string[]
              )[0];
            }
            break;

          default:
            // Unknown field type - skip
            break;
        }
      });

      // Set the extendedFields object first to initialize it
      setFieldValue("extendedFields", defaultFields);

      // Then set each field individually to ensure they're properly registered with Formik
      Object.entries(defaultFields).forEach(([fieldId, value]) => {
        setFieldValue(`extendedFields['${fieldId}']`, value);
      });
    }
  }, [member, extendedFieldDefinitions, values.extendedFields, setFieldValue]);

  return (
    <Card w="100%">
      <Stack
        w="100%"
        alignItems="start"
        spacing={{ base: 4, xl: 24 }}
        bgColor="gray.600"
        py={5}
        px={8}
        borderTopStartRadius="3xl"
        borderTopEndRadius="3xl"
        direction={{ base: "column", xl: "row" }}
      >
        <VStack spacing="1" alignItems="flex-start">
          <Text fontSize="2xl" color="white" fontWeight="bold">
            {member != null ? memberFullName : "New Member"}
          </Text>
        </VStack>

        <HStack
          flex="1"
          justifyContent="end"
          position="relative"
          zIndex={100}
          alignSelf="center"
        >
          {(member !== null || !isEmptyStr(values.venueId)) && (
            <>
              <Box>
                <Button
                  colorScheme="cherryButton"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                  onClick={submitForm}
                >
                  Save
                </Button>
              </Box>

              <Box>
                <Button disabled={isSubmitting} onClick={onCancel}>
                  Cancel
                </Button>
              </Box>
            </>
          )}
        </HStack>
      </Stack>
      {member === null && isEmptyStr(values.venueId) && (
        <VStack
          w="100%"
          p={8}
          color="cherryGrey.900"
          bgColor="white"
          borderBottomLeftRadius="3xl"
          borderBottomRightRadius="3xl"
          boxShadow="md"
        >
          <ChakraReactSelectField
            label="Please select the member's venue"
            name="venueId"
            selectedOptionColor="blue"
            isLoading={venuesOptionsLoading}
            isRequired={true}
            options={
              venuesOptions?.map((provider) => {
                return {
                  label: provider.Value!,
                  value: provider.Key!,
                };
              }) || []
            }
            className="relative-listbox"
          />
        </VStack>
      )}
      {(member !== null || !isEmptyStr(values.venueId)) && (
        <>
          <Tabs variant="edit" w="100%" isLazy>
            <TabList>
              <HStack spacing={3} overflowX="auto">
                <Tab>Details</Tab>

                <Tab>Address</Tab>

                <Tab>Contact</Tab>

                {extendedFieldDefinitions &&
                  extendedFieldDefinitions.length > 0 && (
                    <Tab>Extended Fields</Tab>
                  )}
              </HStack>
            </TabList>

            <TabPanels>
              <TabPanel>
                <Stack
                  w="100%"
                  align="start"
                  spacing={2}
                  direction={["column", "row"]}
                >
                  <VStack align="start" w="100%">
                    <Box
                      w="100%"
                      p={4}
                      border="1px solid"
                      borderColor="cherryButtonDark.50"
                      borderRadius="md"
                    >
                      <HStack width="100%" mb={2}>
                        <Text fontSize="sm" flex="1">
                          <b>Personal</b> :
                        </Text>
                      </HStack>
                      <Divider mb={2} />
                      <VStack
                        w="100%"
                        alignItems="flex-start"
                        spacing={6}
                        pb={10}
                      >
                        {titlesOptions && titlesOptions.length > 0 && (
                          <ChakraReactSelectField
                            label="Title"
                            name="title"
                            selectedOptionColor="blue"
                            isLoading={titlesOptionsLoading}
                            isRequired={isFieldRequired("title")}
                            options={
                              titlesOptions?.map((provider) => {
                                return {
                                  label: provider.Value!,
                                  value: provider.Key!,
                                };
                              }) || []
                            }
                            className="relative-listbox"
                          />
                        )}
                        {(!titlesOptions || titlesOptions.length == 0) && (
                          <TextField
                            name="title"
                            label="Title"
                            placeholder="Title"
                            isRequired={isFieldRequired("title")}
                          />
                        )}
                        <TextField
                          name="firstName"
                          label="First Name"
                          placeholder="First Name"
                          isRequired={isFieldRequired("firstName")}
                        />
                        <TextField
                          name="preferredName"
                          label="Preferred Name"
                          placeholder="Preferred Name"
                        />

                        <TextField
                          name="middleName"
                          label="Middle Name"
                          placeholder="Middle Name"
                        />
                        <TextField
                          name="lastName"
                          label="Last Name"
                          placeholder="Last Name"
                          isRequired={isFieldRequired("lastName")}
                        />

                        <DateField
                          name="birthDate"
                          label="Birth Date"
                          placeholder="Birth Date"
                          isRequired={isFieldRequired("birthDate")}
                        />

                        {gendersOptions && gendersOptions.length > 0 && (
                          <ChakraReactSelectField
                            label="Gender"
                            name="gender"
                            selectedOptionColor="blue"
                            isLoading={gendersOptionsLoading}
                            isRequired={isFieldRequired("gender")}
                            options={
                              gendersOptions?.map((provider) => {
                                return {
                                  label: provider.Value!,
                                  value: provider.Key!,
                                };
                              }) || []
                            }
                            className="relative-listbox"
                          />
                        )}
                        {(!gendersOptions || gendersOptions.length == 0) && (
                          <TextField
                            name="gender"
                            label="Gender"
                            placeholder="Gender"
                            isRequired={isFieldRequired("gender")}
                          />
                        )}
                      </VStack>
                    </Box>
                  </VStack>

                  <VStack align="start" w="100%">
                    <Box
                      w="100%"
                      p={4}
                      border="1px solid"
                      borderColor="cherryButtonDark.50"
                      borderRadius="md"
                    >
                      <HStack width="100%" mb={2}>
                        <Text fontSize="sm" flex="1">
                          <b>Membership</b> :
                        </Text>
                      </HStack>
                      <Divider mb={2} />
                      <VStack
                        w="100%"
                        alignItems="flex-start"
                        spacing={6}
                        pb={10}
                      >
                        <ChakraReactSelectField
                          label="Membership Type"
                          name="membershipType"
                          selectedOptionColor="blue"
                          isLoading={membershipTypesOptionsLoading}
                          isRequired={isFieldRequired("membershipType")}
                          options={
                            membershipTypesOptions?.map((provider) => {
                              return {
                                label: provider.Value!,
                                value: provider.Key!,
                              };
                            }) || []
                          }
                          className="relative-listbox"
                          onChange={(e) => {
                            const label = (e as SingleValue<SelectOption>)
                              ?.label;
                            setFieldValue("membershipTypeDescription", label);
                          }}
                        />
                        {membershipLevelsOptions &&
                          membershipLevelsOptions.length > 0 && (
                            <ChakraReactSelectField
                              label="Member Level"
                              name="memberLevel"
                              selectedOptionColor="blue"
                              isLoading={membershipLevelsOptionsLoading}
                              options={
                                membershipLevelsOptions?.map((provider) => {
                                  return {
                                    label: provider.Value!,
                                    value: provider.Key!,
                                  };
                                }) || []
                              }
                              className="relative-listbox"
                              onChange={(e) => {
                                const label = (e as SingleValue<SelectOption>)
                                  ?.label;
                                setFieldValue("memberLevelDescription", label);
                              }}
                            />
                          )}
                        {(!membershipLevelsOptions ||
                          membershipLevelsOptions.length == 0) && (
                          <>
                            <TextField
                              name="memberLevel"
                              label="Member Level"
                              placeholder="Member Level"
                            />
                            <TextField
                              name="membershipLevelDescription"
                              label="Member Level Description"
                              placeholder="Member Level Description"
                            />
                          </>
                        )}
                      </VStack>
                    </Box>
                    <Box
                      w="100%"
                      p={4}
                      border="1px solid"
                      borderColor="cherryButtonDark.50"
                      borderRadius="md"
                    >
                      <HStack width="100%" mb={2}>
                        <Text fontSize="sm" flex="1">
                          <b>Other</b> :
                        </Text>
                      </HStack>
                      <Divider mb={2} />
                      <VStack
                        w="100%"
                        alignItems="flex-start"
                        spacing={6}
                        pb={10}
                      >
                        <TextField
                          name="occupation"
                          label="Occupation"
                          placeholder="Occupation"
                          isRequired={isFieldRequired("occupation")}
                        />

                        <TextField
                          name="nationality"
                          label="Nationality"
                          placeholder="Nationality"
                          isRequired={isFieldRequired("nationality")}
                        />
                      </VStack>
                    </Box>
                  </VStack>
                </Stack>
              </TabPanel>

              <TabPanel>
                <VStack align="start" w="100%">
                  <ChakraReactSelectField
                    horizontal
                    label="Default Address"
                    name="defaultAddress"
                    selectedOptionColor="blue"
                    options={AddressTypeList()}
                    fcProps={{ maxW: "50%" }}
                    valueProps={{ flex: 2 }}
                  />
                  <Stack
                    w="100%"
                    align="start"
                    direction={["column", "row"]}
                    spacing={2}
                  >
                    <MemberAddressForm
                      heading="Home Address"
                      addressFieldName="homeAddress"
                      geocodedAddressFieldName="geocodedHomeAddress"
                      address={values.homeAddress}
                      tempGeocodedAddress={values.tempGeocodedAddress}
                      setFieldValue={setFieldValue}
                    />
                    <MemberAddressForm
                      heading="Work Address"
                      addressFieldName="workAddress"
                      geocodedAddressFieldName="geocodedWorkAddress"
                      tempGeocodedAddress={values.tempGeocodedAddress}
                      setFieldValue={setFieldValue}
                    />
                    <MemberAddressForm
                      heading="Postal Address"
                      addressFieldName="postalAddress"
                      geocodedAddressFieldName="geocodedPostalAddress"
                      tempGeocodedAddress={values.tempGeocodedAddress}
                      setFieldValue={setFieldValue}
                    />
                  </Stack>
                </VStack>
              </TabPanel>

              <TabPanel>
                <VStack align="start" w="100%">
                  <ChakraReactSelectField
                    horizontal
                    label="Default Contact Details"
                    name="defaultContactDetails"
                    selectedOptionColor="blue"
                    options={ContactTypeList()}
                    fcProps={{ maxW: "50%" }}
                    valueProps={{ flex: 2 }}
                  />
                  <Stack
                    w="100%"
                    align="start"
                    direction={["column", "row"]}
                    spacing={2}
                  >
                    <MemberContactForm
                      contactFieldName="homeContactDetails"
                      heading="Home Contact Details"
                      isEmailRequired={
                        defaultContactType === ContactType.Home &&
                        isContactFieldRequired("EmailAddress")
                      }
                      isMobileRequired={
                        defaultContactType === ContactType.Home &&
                        isContactFieldRequired("MobileNumber")
                      }
                      isPhoneRequired={
                        defaultContactType === ContactType.Home &&
                        isContactFieldRequired("PhoneNumber")
                      }
                    />
                    <MemberContactForm
                      contactFieldName="workContactDetails"
                      heading="Work Contact Details"
                      isEmailRequired={
                        defaultContactType === ContactType.Work &&
                        isContactFieldRequired("EmailAddress")
                      }
                      isMobileRequired={
                        defaultContactType === ContactType.Work &&
                        isContactFieldRequired("MobileNumber")
                      }
                      isPhoneRequired={
                        defaultContactType === ContactType.Work &&
                        isContactFieldRequired("PhoneNumber")
                      }
                    />
                    <MemberContactForm
                      contactFieldName="postalContactDetails"
                      heading="Postal Contact Details"
                      isEmailRequired={
                        defaultContactType === ContactType.Postal &&
                        isContactFieldRequired("EmailAddress")
                      }
                      isMobileRequired={
                        defaultContactType === ContactType.Postal &&
                        isContactFieldRequired("MobileNumber")
                      }
                      isPhoneRequired={
                        defaultContactType === ContactType.Postal &&
                        isContactFieldRequired("PhoneNumber")
                      }
                    />
                  </Stack>
                </VStack>
              </TabPanel>

              {extendedFieldDefinitions && extendedFieldDefinitions.length > 0 && (
                <TabPanel>
                  <VStack w="100%" align="start" spacing={2}>
                    <Box
                      w="100%"
                      p={4}
                      border="1px solid"
                      borderColor="cherryButtonDark.50"
                      borderRadius="md"
                    >
                      <HStack width="100%" mb={2}>
                        <Text fontSize="sm" flex="1">
                          <b>Extended Fields</b>
                        </Text>
                      </HStack>
                      <Divider mb={2} />
                      <VStack
                        w="100%"
                        alignItems="flex-start"
                        spacing={6}
                        pb={10}
                      >
                        {extendedFieldDefinitionsLoading ? (
                          <Text>Loading extended fields...</Text>
                        ) : (
                          extendedFieldDefinitions
                            .sort(
                              (a, b) =>
                                (a.FieldOrder ?? 0) - (b.FieldOrder ?? 0)
                            )
                            .map((fieldDef) => {
                              const fieldName = `extendedFields['${fieldDef.id}']`;
                              return (
                                <ExtendedFieldInput
                                  key={fieldDef.id}
                                  fieldDefinition={fieldDef}
                                  fieldName={fieldName}
                                />
                              );
                            })
                        )}
                      </VStack>
                    </Box>
                  </VStack>
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
          <HStack width="100%" justifyContent="center" spacing="3" pt={8}>
            <Button
              colorScheme="cherryButton"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting}
              onClick={submitForm}
            >
              Save
            </Button>
            <Button disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
          </HStack>
        </>
      )}
    </Card>
  );
};

export const MemberForm = withFormik<MemberFormProps, MemberFormValues>({
  mapPropsToValues: ({ member }) => {
    // Extended fields are returned as a flat object with field IDs as keys
    // For editing existing members, use their current values
    // For new members, defaults will be populated by useEffect after definitions load
    const extendedFields: { [key: string]: unknown } | undefined =
      member?.ExtendedFields
        ? Object.fromEntries(
            Object.entries(member.ExtendedFields).map(([key, value]) => {
              // Normalize number arrays/values to strings for select compatibility
              if (Array.isArray(value)) {
                return [
                  key,
                  value.map((v) => (typeof v === "number" ? v.toString() : v)),
                ];
              }
              if (typeof value === "number") {
                return [key, value.toString()];
              }
              return [key, value];
            })
          )
        : undefined;

    return {
      memberKey: member?.MemberKey,
      membershipNumber: member?.MembershipNumber,
      badgeNumber: member?.BadgeNumber,
      title: member?.Title,
      cardNumber: member?.CardNumber,
      firstName: member?.FirstName,
      preferredName: member?.PreferredName,
      middleName: member?.MiddleName,
      lastName: member?.LastName,
      birthDate: member?.BirthDate,
      nationality: member?.Nationality,
      dateJoined: member?.DateJoined,
      dateRenewed: member?.DateRenewed,
      dateFinancial: member?.DateFinancial,
      financial: member?.Financial,
      status: member?.Status,
      gender: member?.Gender,
      occupation: member?.Occupation,
      memberLevel: member?.MemberLevel,
      memberLevelDescription: member?.MemberLevelDescription,
      membershipType: member?.MembershipType,
      membershipTypeDescription: member?.MembershipTypeDescription,
      defaultAddress: member?.DefaultAddress as AddressType,
      homeAddress: member?.HomeAddress ?? undefined,
      workAddress: member?.WorkAddress ?? undefined,
      postalAddress: member?.PostalAddress ?? undefined,
      defaultContactDetails: member?.DefaultContactDetails as ContactType,
      homeContactDetails: member?.HomeContactDetails,
      workContactDetails: member?.WorkContactDetails,
      postalContactDetails: member?.PostalContactDetails,
      pointsBalance: member?.PointsBalance,
      extendedFields: extendedFields,
    };
  },

  handleSubmit: async (values, { props, setFieldError, setErrors }) => {
    let isInvalid = false;
    //only validate venue when new member
    if (isEmptyStr(values.venueId) && isEmptyStr(values.memberKey)) {
      setFieldError("venueId", "Venue is required");
      isInvalid = true;
    }

    if (isEmptyStr(values.membershipType)) {
      setFieldError("membershipType", "Membership Type is required");
      isInvalid = true;
    }

    if (isEmptyStr(values.firstName)) {
      setFieldError("firstName", "First Name is required");
      isInvalid = true;
    }

    if (isEmptyStr(values.lastName)) {
      setFieldError("lastName", "Last Name is required");
      isInvalid = true;
    }

    // Validate dynamic required fields from venue configuration (only for new members)
    if (
      isEmptyStr(values.memberKey) &&
      values.portalRequiredFields &&
      values.portalRequiredFields.length > 0
    ) {
      // Determine which contact details type to use for contact field validation
      const defaultContactType =
        values.defaultContactDetails || ContactType.Home;
      const contactFieldPrefix = `${defaultContactType.toLowerCase()}ContactDetails`;

      // Get the contact details object based on default type
      const getContactDetails = (): CherryPayApi.Contact | undefined => {
        switch (defaultContactType) {
          case ContactType.Home:
            return values.homeContactDetails;
          case ContactType.Work:
            return values.workContactDetails;
          case ContactType.Postal:
            return values.postalContactDetails;
          default:
            return values.homeContactDetails;
        }
      };

      const contactDetails = getContactDetails();

      values.portalRequiredFields.forEach((field) => {
        switch (field) {
          case "BirthDate":
            if (isEmptyStr(values.birthDate)) {
              setFieldError("birthDate", "Birth Date is required");
              isInvalid = true;
            }
            break;
          case "Gender":
            if (isEmptyStr(values.gender)) {
              setFieldError("gender", "Gender is required");
              isInvalid = true;
            }
            break;
          case "Title":
            if (isEmptyStr(values.title)) {
              setFieldError("title", "Title is required");
              isInvalid = true;
            }
            break;
          case "Occupation":
            if (isEmptyStr(values.occupation)) {
              setFieldError("occupation", "Occupation is required");
              isInvalid = true;
            }
            break;
          case "Nationality":
            if (isEmptyStr(values.nationality)) {
              setFieldError("nationality", "Nationality is required");
              isInvalid = true;
            }
            break;
          case "EmailAddress":
            if (isEmptyStr(contactDetails?.EmailAddress)) {
              setFieldError(
                `${contactFieldPrefix}.EmailAddress`,
                "Email Address is required"
              );
              isInvalid = true;
            }
            break;
          case "MobileNumber":
            if (isEmptyStr(contactDetails?.MobileNumber)) {
              setFieldError(
                `${contactFieldPrefix}.MobileNumber`,
                "Mobile Number is required"
              );
              isInvalid = true;
            }
            break;
          case "PhoneNumber":
            if (isEmptyStr(contactDetails?.PhoneNumber)) {
              setFieldError(
                `${contactFieldPrefix}.PhoneNumber`,
                "Phone Number is required"
              );
              isInvalid = true;
            }
            break;
        }
      });
    }

    if (
      values.homeContactDetails &&
      !isEmptyStr(values.homeContactDetails.EmailAddress) &&
      !isValidEmail(values.homeContactDetails.EmailAddress)
    ) {
      setFieldError(
        "homeContactDetails.EmailAddress",
        "Invalid home email address"
      );
    }

    if (
      values.workContactDetails &&
      !isEmptyStr(values.workContactDetails.EmailAddress) &&
      !isValidEmail(values.workContactDetails.EmailAddress)
    ) {
      setFieldError(
        "workContactDetails.EmailAddress",
        "Invalid work email address"
      );
    }

    if (
      values.postalContactDetails &&
      !isEmptyStr(values.postalContactDetails.EmailAddress) &&
      !isValidEmail(values.postalContactDetails.EmailAddress)
    ) {
      setFieldError(
        "postalContactDetails.EmailAddress",
        "Invalid postal email address"
      );
    }

    // Validate required extended fields
    if (
      props.extendedFieldDefinitions &&
      props.extendedFieldDefinitions.length > 0
    ) {
      const extendedFieldErrors: { [key: string]: string } = {};

      props.extendedFieldDefinitions.forEach((fieldDef) => {
        const fieldValue = values.extendedFields?.[fieldDef.id];

        // Check if the field is required based on its type-specific attributes
        const isRequired =
          fieldDef.BooleanAttributes?.IsRequired ||
          fieldDef.DateAttributes?.IsRequired ||
          fieldDef.NumberAttributes?.IsRequired ||
          fieldDef.StringAttributes?.IsRequired ||
          fieldDef.TimeAttributes?.IsRequired ||
          false;

        if (isRequired) {
          // Check if value is missing or empty
          if (
            fieldValue === null ||
            fieldValue === undefined ||
            fieldValue === ""
          ) {
            // Build the error object with the field ID as the key
            extendedFieldErrors[
              fieldDef.id
            ] = `${fieldDef.FieldName} is required`;
            isInvalid = true;
          }
        }

        // Validate regex pattern for Text fields
        if (
          (fieldDef.FieldType === "Text" ||
            fieldDef.FieldType === "TextArea") &&
          fieldDef.StringAttributes?.RegexPattern &&
          fieldValue !== null &&
          fieldValue !== undefined &&
          fieldValue !== ""
        ) {
          try {
            const regex = new RegExp(fieldDef.StringAttributes.RegexPattern);
            if (!regex.test(String(fieldValue))) {
              extendedFieldErrors[
                fieldDef.id
              ] = `${fieldDef.FieldName} does not match the required format`;
              isInvalid = true;
            }
          } catch (error) {
            console.error(
              `Invalid regex pattern for field ${fieldDef.FieldName}:`,
              error
            );
          }
        }

        if (
          (fieldDef.FieldType === "Number" ||
            fieldDef.FieldType === "NumberSelection") &&
          fieldValue !== null &&
          fieldValue !== undefined &&
          fieldValue !== ""
        ) {
          const numValue: number =
            typeof fieldValue === "string"
              ? parseFloat(fieldValue)
              : Number(fieldValue);

          if (isNaN(numValue)) {
            extendedFieldErrors[
              fieldDef.id
            ] = `${fieldDef.FieldName} must be a valid number`;
            isInvalid = true;
          } else {
            if (
              fieldDef.NumberAttributes?.MinValue !== null &&
              fieldDef.NumberAttributes?.MinValue !== undefined &&
              numValue < fieldDef.NumberAttributes.MinValue
            ) {
              extendedFieldErrors[
                fieldDef.id
              ] = `${fieldDef.FieldName} must be at least ${fieldDef.NumberAttributes.MinValue}`;
              isInvalid = true;
            }

            if (
              fieldDef.NumberAttributes?.MaxValue !== null &&
              fieldDef.NumberAttributes?.MaxValue !== undefined &&
              numValue > fieldDef.NumberAttributes.MaxValue
            ) {
              extendedFieldErrors[
                fieldDef.id
              ] = `${fieldDef.FieldName} must be at most ${fieldDef.NumberAttributes.MaxValue}`;
              isInvalid = true;
            }
          }
        }

        if (
          fieldDef.FieldType === "NumberMultiSelection" &&
          Array.isArray(fieldValue) &&
          fieldValue.length > 0
        ) {
          for (let i = 0; i < fieldValue.length; i++) {
            const numValue: number =
              typeof fieldValue[i] === "string"
                ? parseFloat(fieldValue[i])
                : Number(fieldValue[i]);

            if (isNaN(numValue)) {
              extendedFieldErrors[
                fieldDef.id
              ] = `${fieldDef.FieldName} must contain valid numbers only`;
              isInvalid = true;
              break;
            }
          }
        }
      });

      // If there are extended field errors, set them on the form
      if (Object.keys(extendedFieldErrors).length > 0) {
        // Set each field error individually using bracket notation to match the field name format
        Object.entries(extendedFieldErrors).forEach(
          ([fieldId, errorMessage]) => {
            setFieldError(`extendedFields['${fieldId}']`, errorMessage);
          }
        );
      }
    }

    if (isInvalid) {
      props.onValidationError?.(
        "One or more fields are either not complete or invalid. Please check each field and try again."
      );
      return;
    }

    const memberUpdate: Partial<CherryPayApi.MemberUpdate> = {
      MemberKey: values.memberKey ?? uuid().replace(/-/g, ""),
      MembershipNumber: emptyStrToNull(values.membershipNumber),
      BadgeNumber: emptyStrToNull(values.badgeNumber),
      CardNumber: emptyStrToNull(values.cardNumber),
      Title: emptyStrToNull(values.title),
      FirstName: emptyStrToNull(values.firstName),
      PreferredName: emptyStrToNull(values.preferredName),
      MiddleName: emptyStrToNull(values.middleName),
      LastName: emptyStrToNull(values.lastName),
      BirthDate: emptyStrToNull(values.birthDate),
      Nationality: emptyStrToNull(values.nationality),
      DateJoined: emptyStrToNull(values.dateJoined),
      DateRenewed: emptyStrToNull(values.dateRenewed),
      DateFinancial: emptyStrToNull(values.dateFinancial),
      Financial: values.financial,
      Status: emptyStrToNull(values.status),
      Gender: emptyStrToNull(values.gender),
      Occupation: emptyStrToNull(values.occupation),
      MemberLevel: emptyStrToNull(values.memberLevel),
      MemberLevelDescription: emptyStrToNull(values.memberLevelDescription),
      MembershipType: emptyStrToNull(values.membershipType),
      MembershipTypeDescription: emptyStrToNull(
        values.membershipTypeDescription
      ),
      DefaultAddress: values.defaultAddress,
      HomeAddress: emptyStringValuesToNull(values.homeAddress) ?? undefined,
      WorkAddress: emptyStringValuesToNull(values.workAddress) ?? undefined,
      PostalAddress: emptyStringValuesToNull(values.postalAddress) ?? undefined,
      DefaultContactDetails: values.defaultContactDetails,
      HomeContactDetails:
        emptyStringValuesToNull(values.homeContactDetails) ?? undefined,
      WorkContactDetails:
        emptyStringValuesToNull(values.workContactDetails) ?? undefined,
      PostalContactDetails:
        emptyStringValuesToNull(values.postalContactDetails) ?? undefined,
      PointsBalance: values.pointsBalance,
    };

    // Handle extended fields - convert to correct types based on field definitions
    // The values.extendedFields should have full field IDs as keys
    // e.g. { "DEV.Ext-CustomField-Text-BikeBrand": "Santa Cruz" }
    if (
      values.extendedFields &&
      Object.keys(values.extendedFields).length > 0
    ) {
      const convertedFields: { [key: string]: unknown } = {};

      // If we have field definitions, convert values to correct types
      if (props.extendedFieldDefinitions) {
        // Create a map of field ID to field definition for quick lookup
        const definitionsMap = new Map<
          string,
          CherryPayApi.ExtendedFieldDefinition
        >();
        props.extendedFieldDefinitions.forEach((def) => {
          definitionsMap.set(def.id, def);
        });

        // Convert each field value based on its type
        Object.entries(values.extendedFields).forEach(([key, value]) => {
          const fieldDef = definitionsMap.get(key);

          // Skip empty values
          if (value === null || value === undefined || value === "") {
            return;
          }

          if (fieldDef) {
            switch (fieldDef.FieldType) {
              case "Number":
              case "NumberSelection":
              case "NumberMultiSelection":
                // Convert to number or array of numbers
                if (Array.isArray(value)) {
                  const numArray = value
                    .map((v) => (typeof v === "string" ? parseFloat(v) : v))
                    .filter((v) => !isNaN(v as number));
                  if (numArray.length > 0) {
                    convertedFields[key] = numArray;
                  }
                } else {
                  const numValue =
                    typeof value === "string" ? parseFloat(value) : value;
                  if (!isNaN(numValue as number)) {
                    convertedFields[key] = numValue;
                  }
                }
                break;

              case "Toggle":
                // Ensure boolean type
                convertedFields[key] = Boolean(value);
                break;

              case "Date":
                // Date field stores YYYY-MM-DD format string
                // Send without timezone suffix so server treats it as timezone-agnostic
                if (typeof value === "string" && value) {
                  // Match YYYY-MM-DD at the start (handles both "YYYY-MM-DD" and "YYYY-MM-DDTHH:mm:ss..." formats)
                  const dateMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
                  if (dateMatch) {
                    // Convert to ISO format WITHOUT timezone: YYYY-MM-DDT00:00:00
                    convertedFields[key] = `${dateMatch[1]}T00:00:00`;
                  }
                }
                break;

              case "DateTime":
                // DateTimeField component stores timezone-agnostic ISO strings (YYYY-MM-DDTHH:mm:ss)
                // Send without timezone suffix so server treats it as timezone-agnostic
                if (typeof value === "string" && value) {
                  // Clean and return the ISO string (strips milliseconds/Z suffix)
                  convertedFields[key] =
                    DateUtil.getTimezoneAgnosticDateTimeFormat(value);
                } else if (value instanceof Date && !isNaN(value.getTime())) {
                  // Fallback for Date objects (backward compatibility)
                  // Extract components without timezone conversion
                  convertedFields[key] =
                    DateUtil.dateToTimezoneAgnosticISO(value);
                }
                break;

              case "Time":
                // Time field stores HH:mm format string
                // Send as HH:mm format (not full datetime)
                if (typeof value === "string" && value) {
                  // Validate it's in HH:mm format
                  const timeMatch = value.match(
                    /^(\d{2}):(\d{2})(?::(\d{2}))?$/
                  );
                  if (timeMatch) {
                    const hours = timeMatch[1];
                    const minutes = timeMatch[2];
                    // Format as HH:mm
                    convertedFields[key] = `${hours}:${minutes}`;
                  }
                }
                break;

              case "Text":
              case "TextArea":
              case "TextSelection":
              case "TextMultiSelection":
                // Keep as string or array of strings
                if (Array.isArray(value)) {
                  convertedFields[key] = value.map((v) => String(v));
                } else {
                  convertedFields[key] = String(value);
                }
                break;

              default:
                // Unknown type - keep as-is
                convertedFields[key] = value;
            }
          } else {
            // No definition found - keep as-is
            convertedFields[key] = value;
          }
        });

        memberUpdate.ExtendedFields = convertedFields;
      } else {
        // No definitions available - send as-is
        memberUpdate.ExtendedFields = values.extendedFields;
      }
    }

    let result: ApiResult<CherryPayApi.Member> | null = null;

    if (props.member == null) {
      result = await props.apiClient.createHubMembershipMember(
        memberUpdate,
        props.businessId,
        {
          venueId: values.venueId,
        }
      );
    } else {
      result = await props.apiClient.updateHubMembershipMember(
        memberUpdate,
        props.businessId,
        props.member.id
      );
    }

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess(result.data);
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }
  },

  validateOnMount: false,
  validateOnBlur: true,

  validate: (values) => {
    let errors: FormikErrors<MemberFormValues> = {};

    return errors;
  },
})(InnerForm);
